import { Link, Avatar as MuiAvatar, SxProps, Tooltip, Typography, styled, useTheme } from "@mui/material";
import { Links } from "../constants";
import { IUser } from "../models";

const XSmallPhotoSizeInPx = 20;
const SmallPhotoSizeInPx = 32;
const LargePhotoSizeInPx = 54;

const XSmallBorderSizeInPx = 2;
const SmallBorderSizeInPx = 2;
const LargeBorderSizeInPx = 3;

const XSmallStringAvatarSizeInPx = XSmallPhotoSizeInPx + 2 * XSmallBorderSizeInPx;
const SmallStringAvatarSizeInPx = SmallPhotoSizeInPx + 2 * SmallBorderSizeInPx;
const LargeStringAvatarSizeInPx = LargePhotoSizeInPx + 2 * LargeBorderSizeInPx;

type AvatarSize = "xsmall" | "small" | "large" | undefined;

const getStringAvatarSizeInPx = (size: AvatarSize) => {
  switch (size) {
    case "xsmall":
      return `${XSmallStringAvatarSizeInPx}px`;
    case "small":
      return `${SmallStringAvatarSizeInPx}px`;
    case "large":
    default:
      return `${LargeStringAvatarSizeInPx}px`;
  }
};

const getPhotoAvatarSizeInPx = (size: AvatarSize) => {
  switch (size) {
    case "xsmall":
      return `${XSmallPhotoSizeInPx}px`;
    case "small":
      return `${SmallPhotoSizeInPx}px`;
    case "large":
    default:
      return `${LargePhotoSizeInPx}px`;
  }
};

const getPhotoAvatarBorderWidth = (size: AvatarSize) => {
  switch (size) {
    case "xsmall":
      return `${XSmallBorderSizeInPx}px`;
    case "small":
      return `${SmallBorderSizeInPx}px`;
    case "large":
    default:
      return `${LargeBorderSizeInPx}px`;
  }
};

const StyledMuiAvatar = styled(MuiAvatar)<{
  $border: boolean | undefined;
  $size: AvatarSize;
}>`
  box-sizing: content-box;
  height: ${({ $size }) => getPhotoAvatarSizeInPx($size)};
  width: ${({ $size }) => getPhotoAvatarSizeInPx($size)};

  ${({ $border, $size, theme }) =>
    $border &&
    `
      background: linear-gradient(315deg, ${theme.palette.wine.main}, ${theme.palette.error.main}) border-box;
      border-color: transparent;
      border-style: solid;
      border-width: ${getPhotoAvatarBorderWidth($size)};

      &:before {
        border-color: #fff;
        border-radius: 50%;
        border-style: solid;
        border-width: ${getPhotoAvatarBorderWidth($size)};
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        content: "";
      }
    `}
`;

interface IProps {
  user: IUser;
  tooltip?: boolean;
  border?: boolean;
  size?: AvatarSize;
  sx?: SxProps;
}

const Avatar = (props: IProps) => {
  const { size = "small" } = props; // Define a default value of "small" for the variant prop
  const theme = useTheme();

  const profilePhotoUrl =
    size === "large"
      ? props.user?.profilePhotoLargeUrl ?? props.user?.profilePhotoSmallUrl
      : props.user?.profilePhotoSmallUrl;

  function stringAvatar(name: string) {
    const firstName = name.split(" ")[0][0];
    const lastName = name.split(" ")[1]?.[0] ?? "";

    return {
      sx: {
        ...props.sx,
        background: props.user!.enabled
          ? `linear-gradient(315deg, ${theme.palette.wine.main}, ${theme.palette.error.main}) border-box`
          : theme.palette.grey[500],
        height: getStringAvatarSizeInPx(size),
        width: getStringAvatarSizeInPx(size),
      } as SxProps,
      children: (
        <Typography>
          {firstName}
          {lastName}
        </Typography>
      ),
    };
  }

  function drawAvatar() {
    return profilePhotoUrl ? (
      <StyledMuiAvatar
        $border={props.border}
        $size={size}
        sx={{ ...props.sx }}
        style={!props.user.enabled ? { filter: "grayscale(100%)" } : {}}
        alt={props.user.displayName}
        src={profilePhotoUrl}
      />
    ) : (
      <StyledMuiAvatar
        $border={undefined}
        $size={size}
        alt={props.user.displayName}
        {...stringAvatar(props.user.displayName)}
      />
    );
  }

  return props.tooltip ? (
    <Tooltip title={<Link href={Links.User(props.user.userId)}>{props.user.displayName}</Link>}>{drawAvatar()}</Tooltip>
  ) : (
    drawAvatar()
  );
};

export { Avatar };
export type { AvatarSize };
